// CRM-NANCOZI — Module Formations
import { useEffect, useState } from 'react';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchFormations, createFormation, updateFormation, archiverFormation, activerFormation,
  fetchInscriptions, createInscription, updateInscription, supprimerInscription,
} from '../store/slices/formationsSlice';
import { fetchClients } from '../store/slices/clientsSlice';
import { toast } from 'react-toastify';
import {
  Plus, Search, Pencil, Archive, RotateCcw, Trash2,
  BookOpen, Users, TrendingUp, CheckCircle, UserPlus, ListChecks,
  Printer, Download,
} from 'lucide-react';

/* ── Constantes ── */
const DOMAINES = {
  action:     'ACTION',
  obligation: 'OBLIGATION',
  autre:      'AUTRE',
};
const PAIEMENTS = {
  paye:    { label: 'Payé',    badge: 'vert'   },
  partiel: { label: 'Partiel', badge: 'orange' },
  impaye:  { label: 'Impayé',  badge: 'rouge'  },
};
const MODES = {
  especes:      'Espèces',
  virement:     'Virement',
  mobile_money: 'Mobile Money',
};

const fmt  = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtN = (n) => Number(n || 0).toLocaleString('fr-FR');

/* ═══════════════════════════════════════════════
   Modal — Créer / Modifier une Formation
═══════════════════════════════════════════════ */
function ModalFormation({ formation, onClose, onSaved }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    code_formation: '',
    intitule:       '',
    description:    '',
    domaine:        'action',
    duree_heures:   '',
    prix:           '',
    statut:         'active',
    ...formation,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      if (formation?.id) {
        await dispatch(updateFormation({ id: formation.id, payload: form })).unwrap();
        toast.success('Formation mise à jour.');
      } else {
        await dispatch(createFormation(form)).unwrap();
        toast.success('Formation créée.');
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err && typeof err === 'object') setErrors(err);
      else toast.error('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const fieldErr = (k) => errors[k] ? (
    <div style={{ color: '#C00000', fontSize: 11, marginTop: 2 }}>
      {Array.isArray(errors[k]) ? errors[k][0] : errors[k]}
    </div>
  ) : null;

  const Lbl = ({ children }) => (
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>
      {children}
    </label>
  );
  const inp = { fontSize: 12, padding: '6px 10px' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 680 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{formation?.id ? 'Modifier la formation' : 'Nouvelle formation'}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">

          {/* 2 colonnes : champs | description */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

            {/* Col 1 : champs principaux */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
                <div>
                  <Lbl>Code *</Lbl>
                  <input className="form-input" style={inp} value={form.code_formation}
                    onChange={e => set('code_formation', e.target.value)} required placeholder="FIN-001" />
                  {fieldErr('code_formation')}
                </div>
                <div>
                  <Lbl>Intitulé *</Lbl>
                  <input className="form-input" style={inp} value={form.intitule}
                    onChange={e => set('intitule', e.target.value)} required />
                  {fieldErr('intitule')}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <Lbl>Produits *</Lbl>
                  <select className="form-input" style={inp} value={form.domaine}
                    onChange={e => set('domaine', e.target.value)}>
                    {Object.entries(DOMAINES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <Lbl>Durée (heures) *</Lbl>
                  <input className="form-input" style={inp} type="number" min="1"
                    value={form.duree_heures} onChange={e => set('duree_heures', e.target.value)} required />
                  {fieldErr('duree_heures')}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: formation?.id ? '1fr 1fr' : '1fr', gap: 8 }}>
                <div>
                  <Lbl>Prix (FCFA) *</Lbl>
                  <input className="form-input" style={inp} type="number" min="0"
                    value={form.prix} onChange={e => set('prix', e.target.value)} required />
                  {fieldErr('prix')}
                </div>
                {formation?.id && (
                  <div>
                    <Lbl>Statut</Lbl>
                    <select className="form-input" style={inp} value={form.statut}
                      onChange={e => set('statut', e.target.value)}>
                      <option value="active">Active</option>
                      <option value="archivee">Archivée</option>
                    </select>
                  </div>
                )}
              </div>

            </div>

            {/* Col 2 : description */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Lbl>Description</Lbl>
              <textarea className="form-input" value={form.description}
                onChange={e => set('description', e.target.value)}
                style={{ flex: 1, resize: 'none', fontFamily: 'inherit', fontSize: 12,
                  padding: '6px 10px', minHeight: 120 }}
                placeholder="Objectifs, public cible, contenu…" />
            </div>

          </div>

          <div className="modal-footer" style={{ marginTop: 14 }}>
            <button type="button" className="header-btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="header-btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement…' : formation?.id ? 'Mettre à jour' : 'Créer la formation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Modal — Inscrire un client à une formation
═══════════════════════════════════════════════ */
function ModalInscription({ inscription, preFormation, formations, clients, onClose, onSaved }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [alertMsg, setAlertMsg] = useState('');
  const [form, setForm] = useState({
    client:             inscription?.client     || '',
    formation:          inscription?.formation  || preFormation || '',
    date_participation: inscription?.date_participation || '',
    montant_paye:       inscription?.montant_paye ?? '',
    mode_paiement:      inscription?.mode_paiement || 'especes',
    statut_paiement:    inscription?.statut_paiement || 'impaye',
  });

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (k === 'client' || k === 'formation') setAlertMsg('');
  };

  const formationChoisie = formations.find(f => f.id === Number(form.formation));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setAlertMsg('');
    setSaving(true);
    try {
      if (inscription?.id) {
        await dispatch(updateInscription({ id: inscription.id, payload: form })).unwrap();
        toast.success('Inscription mise à jour.');
      } else {
        await dispatch(createInscription(form)).unwrap();
        toast.success('Client inscrit avec succès.');
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err && typeof err === 'object') {
        const nfe = err.non_field_errors;
        if (nfe) {
          const msg = Array.isArray(nfe) ? nfe[0] : nfe;
          const isDouble = msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('ensemble unique');
          setAlertMsg(isDouble
            ? 'Ce client est déjà inscrit à cette formation. Chaque client ne peut être inscrit qu\'une seule fois par formation.'
            : msg);
        } else {
          setErrors(err);
        }
      } else {
        toast.error('Erreur lors de l\'inscription.');
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldErr = (k) => errors[k] ? (
    <div style={{ color: '#C00000', fontSize: 11, marginTop: 2 }}>
      {Array.isArray(errors[k]) ? errors[k][0] : errors[k]}
    </div>
  ) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {inscription?.id
              ? 'Modifier l\'inscription'
              : preFormation
                ? `Inscrire — ${formations.find(f => f.id === preFormation)?.intitule || 'Formation'}`
                : 'Inscrire un client'}
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-body">

          {alertMsg && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
              padding: '10px 14px', color: '#991B1B', fontSize: 12, lineHeight: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12,
            }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
              <span>{alertMsg}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Client *</label>
              <select className="form-input" value={form.client}
                onChange={e => set('client', e.target.value)} required disabled={!!inscription?.id}>
                <option value="">— Sélectionner un client —</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.nom_cli} {c.prenom_cli}</option>
                ))}
              </select>
              {fieldErr('client')}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Formation *</label>
              <select className="form-input" value={form.formation}
                onChange={e => set('formation', e.target.value)} required disabled={!!inscription?.id}>
                <option value="">— Sélectionner une formation —</option>
                {formations.filter(f => f.statut === 'active').map(f => (
                  <option key={f.id} value={f.id}>[{f.code_formation}] {f.intitule} — {fmtN(f.prix)} FCFA</option>
                ))}
              </select>
              {fieldErr('formation')}
              {formationChoisie && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  Prix : {fmtN(formationChoisie.prix)} FCFA · Durée : {formationChoisie.duree_heures}h
                </div>
              )}
            </div>
            <div>
              <label className="form-label">Date de participation</label>
              <input className="form-input" type="date" value={form.date_participation}
                onChange={e => set('date_participation', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Montant payé (FCFA)</label>
              <input className="form-input" type="number" min="0" value={form.montant_paye}
                onChange={e => set('montant_paye', e.target.value)} placeholder="0" />
              {fieldErr('montant_paye')}
            </div>
            <div>
              <label className="form-label">Mode paiement</label>
              <select className="form-input" value={form.mode_paiement} onChange={e => set('mode_paiement', e.target.value)}>
                {Object.entries(MODES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Statut paiement</label>
              <select className="form-input" value={form.statut_paiement} onChange={e => set('statut_paiement', e.target.value)}>
                {Object.entries(PAIEMENTS).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
          </div>

          <div className="modal-footer" style={{ marginTop: 14 }}>
            <button type="button" className="header-btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="header-btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement…' : inscription?.id ? 'Mettre à jour' : 'Inscrire'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Modal — Liste des participants d'une formation
═══════════════════════════════════════════════ */
function ModalParticipants({ formation, inscriptions, onClose, onInscrire, onRefresh, dispatch }) {
  const participants = (inscriptions || []).filter(ins => ins.formation === formation.id);
  const caTotal = participants.reduce((s, ins) => s + Number(ins.montant_paye || 0), 0);
  const [deleting, setDeleting] = useState({});

  const handleSupprimer = async (ins) => {
    if (!window.confirm(`Supprimer l'inscription de ${ins.client_nom} ?`)) return;
    setDeleting(d => ({ ...d, [ins.id]: true }));
    try {
      await dispatch(supprimerInscription(ins.id)).unwrap();
      toast.success(`Inscription de ${ins.client_nom} supprimée.`);
      onRefresh();
    } catch {
      toast.error('Erreur lors de la suppression.');
    } finally {
      setDeleting(d => ({ ...d, [ins.id]: false }));
    }
  };

  const handleImprimer = () => {
    const now = new Date().toLocaleDateString('fr-FR');
    const lignes = participants.map((ins, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${ins.client_nom || ''}</td>
        <td>${ins.client_telephone || '—'}</td>
        <td>${ins.client_email || '—'}</td>
        <td>${ins.date_inscription ? new Date(ins.date_inscription).toLocaleDateString('fr-FR') : '—'}</td>
        <td>${ins.date_participation ? new Date(ins.date_participation).toLocaleDateString('fr-FR') : '—'}</td>
        <td style="text-align:right">${Number(ins.montant_paye || 0).toLocaleString('fr-FR')} FCFA</td>
        <td>${MODES[ins.mode_paiement] || '—'}</td>
        <td>${PAIEMENTS[ins.statut_paiement]?.label || ins.statut_paiement}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Participants — ${formation.intitule}</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:11px;color:#1F2937;margin:20px}
        h2{font-size:14px;color:#1F4E79;margin:0 0 4px}
        .sub{color:#6B7280;font-size:10px;margin-bottom:12px}
        .stats{display:flex;gap:24px;padding:8px 12px;background:#F8FAFC;
               border:1px solid #E5E7EB;border-radius:6px;margin-bottom:12px}
        .s-val{font-size:16px;font-weight:800;color:#1F4E79}
        .s-lbl{font-size:9px;color:#6B7280}
        table{width:100%;border-collapse:collapse}
        th{background:#1F4E79;color:#fff;padding:6px 5px;text-align:left;font-size:10px}
        td{padding:5px;font-size:10px;border-bottom:1px solid #E5E7EB}
        tr:nth-child(even) td{background:#F8FAFC}
        .foot{margin-top:12px;font-size:9px;color:#9CA3AF;text-align:right}
        @page{margin:12mm;size:A4 landscape}
      </style></head><body>
      <h2>Liste des participants — ${formation.intitule}</h2>
      <div class="sub">
        Code&nbsp;: <b>${formation.code_formation}</b> &nbsp;·&nbsp;
        Produits&nbsp;: ${DOMAINES[formation.domaine] || formation.domaine} &nbsp;·&nbsp;
        Durée&nbsp;: ${formation.duree_heures}h &nbsp;·&nbsp;
        Prix&nbsp;: ${Number(formation.prix || 0).toLocaleString('fr-FR')}&nbsp;FCFA
      </div>
      <div class="stats">
        <div><div class="s-val">${participants.length}</div><div class="s-lbl">Participants</div></div>
        <div><div class="s-val">${participants.filter(p => p.statut_paiement === 'paye').length}</div><div class="s-lbl">Payé intégral</div></div>
        <div><div class="s-val">${participants.filter(p => p.statut_paiement === 'partiel').length}</div><div class="s-lbl">Partiel</div></div>
        <div><div class="s-val">${participants.filter(p => p.statut_paiement === 'impaye').length}</div><div class="s-lbl">Impayé</div></div>
        <div><div class="s-val">${Number(caTotal).toLocaleString('fr-FR')}&nbsp;FCFA</div><div class="s-lbl">CA encaissé</div></div>
      </div>
      <table>
        <thead><tr>
          <th>N°</th><th>Participant</th><th>Téléphone</th><th>Email</th>
          <th>Inscription</th><th>Participation</th>
          <th>Montant payé</th><th>Mode</th><th>Paiement</th>
        </tr></thead>
        <tbody>${lignes}</tbody>
      </table>
      <div class="foot">Imprimé le ${now} &nbsp;·&nbsp; NANCOZI CORPORATION &nbsp;·&nbsp; CRM-NANCOZI</div>
      <script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>
    </body></html>`;

    const win = window.open('', '_blank', 'width=1100,height=720');
    win.document.write(html);
    win.document.close();
  };

  const handleCSV = () => {
    const entetes = ['N°', 'Participant', 'Téléphone', 'Email', 'Date inscription',
                     'Date participation', 'Montant payé (FCFA)', 'Mode paiement', 'Statut paiement'];
    const lignes = participants.map((ins, i) => [
      i + 1,
      ins.client_nom || '',
      ins.client_telephone || '',
      ins.client_email || '',
      ins.date_inscription   ? new Date(ins.date_inscription).toLocaleDateString('fr-FR')   : '',
      ins.date_participation ? new Date(ins.date_participation).toLocaleDateString('fr-FR') : '',
      Number(ins.montant_paye || 0),
      MODES[ins.mode_paiement] || '',
      PAIEMENTS[ins.statut_paiement]?.label || ins.statut_paiement,
    ]);
    const csv = [entetes, ...lignes]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `participants_${formation.code_formation}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: '98vw', maxWidth: 1500, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div>
            <h3 style={{ margin: 0 }}>Participants — {formation.intitule}</h3>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 3, display: 'flex', gap: 12 }}>
              <span style={{ fontFamily: 'monospace', color: '#1F4E79', fontWeight: 700 }}>{formation.code_formation}</span>
              <span>·</span>
              <span>{DOMAINES[formation.domaine] || formation.domaine}</span>
              <span>·</span>
              <span>{formation.duree_heures}h</span>
              <span>·</span>
              <span style={{ fontWeight: 600 }}>{fmtN(formation.prix)} FCFA</span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 16, padding: '10px 20px', background: '#F8FAFC', borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
          {[
            { label: 'Participants',  value: participants.length, color: '#1F4E79' },
            { label: 'Payé intégral', value: participants.filter(p => p.statut_paiement === 'paye').length,    color: '#1E7145' },
            { label: 'Partiel',       value: participants.filter(p => p.statut_paiement === 'partiel').length, color: '#ED7D31' },
            { label: 'Impayé',        value: participants.filter(p => p.statut_paiement === 'impaye').length,  color: '#C00000' },
            { label: 'CA encaissé',   value: fmtN(caTotal) + ' FCFA', color: '#1E7145' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ textAlign: 'center', minWidth: 90 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
              <div style={{ fontSize: 10, color: '#6B7280' }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {participants.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#6B7280' }}>
              <Users size={36} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }} />
              <p style={{ margin: '0 0 16px', fontSize: 14 }}>Aucun participant inscrit à cette formation.</p>
              {formation.statut === 'active' && (
                <button className="header-btn btn-primary"
                  onClick={() => { onClose(); onInscrire(); }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <UserPlus size={14} /> Inscrire un client
                </button>
              )}
            </div>
          ) : (
            <table style={{ width: '100%', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 32 }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 95 }} />
                <col style={{ width: 95 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 40 }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ fontSize: 11 }}>N°</th>
                  <th style={{ fontSize: 11 }}>Nom du client</th>
                  <th style={{ fontSize: 11 }}>Email</th>
                  <th style={{ fontSize: 11 }}>Téléphone</th>
                  <th style={{ fontSize: 11 }}>Inscription</th>
                  <th style={{ fontSize: 11 }}>Participation</th>
                  <th style={{ fontSize: 11 }}>Montant payé</th>
                  <th style={{ fontSize: 11 }}>Mode</th>
                  <th style={{ fontSize: 11 }}>Paiement</th>
                  <th style={{ fontSize: 11 }}></th>
                </tr>
              </thead>
              <tbody>
                {participants.map((ins, i) => (
                  <tr key={ins.id}>
                    <td style={{ fontSize: 11, fontWeight: 700, color: '#1F4E79', padding: '8px 6px' }}>{i + 1}</td>
                    <td style={{ padding: '8px 6px', overflow: 'hidden' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ins.client_nom}
                      </div>
                    </td>
                    <td style={{ fontSize: 11, padding: '8px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', color: '#2E75B6' }}>
                      {ins.client_email || '—'}
                    </td>
                    <td style={{ fontSize: 11, padding: '8px 6px' }}>{ins.client_telephone || '—'}</td>
                    <td style={{ fontSize: 11, padding: '8px 6px' }}>{fmt(ins.date_inscription)}</td>
                    <td style={{ fontSize: 11, padding: '8px 6px', fontWeight: 600, color: '#1F4E79' }}>{fmt(ins.date_participation)}</td>
                    <td style={{ fontSize: 11, padding: '8px 6px', fontWeight: 600 }}>{fmtN(ins.montant_paye)} FCFA</td>
                    <td style={{ fontSize: 11, padding: '8px 6px' }}>{MODES[ins.mode_paiement] || '—'}</td>
                    <td style={{ padding: '8px 6px' }}>
                      <span className={`badge badge-${PAIEMENTS[ins.statut_paiement]?.badge || 'gris'}`} style={{ fontSize: 10 }}>
                        {PAIEMENTS[ins.statut_paiement]?.label || ins.statut_paiement}
                      </span>
                    </td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                      <button className="header-btn btn-danger btn-sm btn-icon"
                        onClick={() => handleSupprimer(ins)}
                        disabled={deleting[ins.id]}
                        title="Supprimer l'inscription">
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-footer" style={{ flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8, flex: 1 }}>
            <button className="header-btn btn-secondary" onClick={handleImprimer}
              disabled={participants.length === 0}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Printer size={14} /> Imprimer / PDF
            </button>
            <button className="header-btn btn-secondary" onClick={handleCSV}
              disabled={participants.length === 0}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Download size={14} /> Exporter CSV
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {formation.statut === 'active' && (
              <button className="header-btn btn-primary"
                onClick={() => { onClose(); onInscrire(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <UserPlus size={14} /> Inscrire un client
              </button>
            )}
            <button className="header-btn btn-secondary" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Page principale
═══════════════════════════════════════════════ */
export default function FormationsPage() {
  const dispatch = useDispatch();
  const { formations, inscriptions, loading } = useSelector(s => s.formations);
  const { liste: clients } = useSelector(s => s.clients);

  const [search, setSearch]                     = useState('');
  const [modalForm, setModalForm]               = useState(null);
  const [modalIns, setModalIns]                 = useState(null);
  const [modalParticipants, setModalParticipants] = useState(null);
  const [actioning, setActioning]               = useState({});

  useEffect(() => {
    dispatch(fetchFormations());
    dispatch(fetchInscriptions());
    dispatch(fetchClients());
  }, [dispatch]);

  useAutoRefresh([fetchFormations, fetchInscriptions], 60000);

  /* ── Filtrage ── */
  const filteredFormations = (formations || []).filter(f => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (f.code_formation || '').toLowerCase().includes(q) ||
      (f.intitule       || '').toLowerCase().includes(q) ||
      (DOMAINES[f.domaine] || '').toLowerCase().includes(q) ||
      (f.statut         || '').toLowerCase().includes(q)
    );
  });

  /* ── KPIs ── */
  const kpis = {
    total:    formations.length,
    actives:  formations.filter(f => f.statut === 'active').length,
    inscrits: inscriptions.length,
    ca:       inscriptions.reduce((s, i) => s + Number(i.montant_paye || 0), 0),
  };

  /* ── Actions ── */
  const doAction = async (key, fn) => {
    setActioning(a => ({ ...a, [key]: true }));
    try { await fn(); } finally { setActioning(a => ({ ...a, [key]: false })); }
  };

  const handleArchiver = (f) => {
    if (!window.confirm(`Archiver la formation "${f.intitule}" ?`)) return;
    doAction(`arch_${f.id}`, async () => {
      await dispatch(archiverFormation(f.id)).unwrap();
      toast.success('Formation archivée.');
      dispatch(fetchFormations());
    });
  };

  const handleActiver = (f) => {
    doAction(`act_${f.id}`, async () => {
      await dispatch(activerFormation(f.id)).unwrap();
      toast.success('Formation réactivée.');
      dispatch(fetchFormations());
    });
  };

  const reload = () => {
    dispatch(fetchFormations());
    dispatch(fetchInscriptions());
  };

  return (
    <div className="page-list">
      {/* ── Barre principale ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div className="search-bar" style={{ flex: '1 1 220px', minWidth: 200 }}>
            <Search size={14} />
            <input
              placeholder="Code, intitulé, domaine, statut…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ fontSize: 12 }}
            />
          </div>

          <div style={{ width: 1, height: 28, background: '#E5E7EB', flexShrink: 0 }} />

          <button className="header-btn btn-primary"
            onClick={() => setModalForm({})}
            style={{ height: 36, padding: '0 14px', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={15} /> Nouvelle formation
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Formations totales',   value: kpis.total,          icon: BookOpen,    color: '#1F4E79' },
          { label: 'Formations actives',   value: kpis.actives,         icon: CheckCircle, color: '#1E7145' },
          { label: 'Inscriptions',         value: kpis.inscrits,        icon: Users,       color: '#ED7D31' },
          { label: 'CA formations (FCFA)', value: fmtN(kpis.ca),        icon: TrendingUp,  color: '#C00000' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card" style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#1F2937' }}>{value}</div>
            </div>
            <div style={{ background: color + '18', borderRadius: 10, padding: 10 }}>
              <Icon size={22} color={color} />
            </div>
          </div>
        ))}
      </div>

      {/* ── Titre section ── */}
      <div style={{ borderBottom: '2px solid #1F4E79', marginBottom: 12, paddingBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1F4E79' }}>Catalogue des formations</span>
      </div>

      {/* ── Tableau ── */}
      <div className="table-container">
        {loading ? (
          <div className="spinner"><div className="spinner-ring" /> Chargement…</div>
        ) : (
          <table style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 32 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: '28%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 72 }} />
              <col style={{ width: 148 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ fontSize: 11 }}>N°</th>
                <th style={{ fontSize: 11 }}>Code</th>
                <th style={{ fontSize: 11 }}>Intitulé</th>
                <th style={{ fontSize: 11 }}>Produits</th>
                <th style={{ fontSize: 11 }}>Durée</th>
                <th style={{ fontSize: 11 }}>Prix (FCFA)</th>
                <th style={{ fontSize: 11 }}>Inscrits</th>
                <th style={{ fontSize: 11 }}>CA (FCFA)</th>
                <th style={{ fontSize: 11 }}>Statut</th>
                <th style={{ fontSize: 11 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFormations.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#6B7280' }}>
                  {search ? 'Aucun résultat' : 'Aucune formation'}
                </td></tr>
              ) : filteredFormations.map((f, i) => (
                <tr key={f.id} style={{ opacity: f.statut === 'archivee' ? 0.6 : 1 }}>
                  <td style={{ fontSize: 11, fontWeight: 700, color: '#1F4E79', padding: '8px 6px' }}>{i + 1}</td>
                  <td style={{ fontSize: 11, fontWeight: 700, padding: '8px 6px', fontFamily: 'monospace', color: '#1F4E79' }}>
                    {f.code_formation}
                  </td>
                  <td style={{ padding: '8px 6px', overflow: 'hidden' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {f.intitule}
                    </div>
                    {f.description && (
                      <div style={{ fontSize: 10, color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {f.description}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {DOMAINES[f.domaine] || f.domaine}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', textAlign: 'center' }}>
                    {f.duree_heures}h
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', fontWeight: 600 }}>
                    {fmtN(f.prix)}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 4px', textAlign: 'center' }}>
                    {f.nb_inscrits > 0
                      ? <span className="badge badge-bleu" style={{ fontSize: 10 }}>{f.nb_inscrits}</span>
                      : <span style={{ color: '#9CA3AF' }}>0</span>}
                  </td>
                  <td style={{ fontSize: 11, padding: '8px 6px', fontWeight: 600, color: '#1E7145' }}>
                    {fmtN(f.ca_total)}
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <span className={`badge badge-${f.statut === 'active' ? 'vert' : 'gris'}`} style={{ fontSize: 10 }}>
                      {f.statut === 'active' ? 'Active' : 'Archivée'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 4px' }}>
                    <div style={{ display: 'flex', gap: 3 }}>
                      <button
                        className="header-btn btn-sm btn-icon"
                        style={{ background: '#EBF3FB', color: '#1F4E79', border: '1px solid #BDD7EE' }}
                        onClick={() => setModalParticipants(f)}
                        title={`Voir les ${f.nb_inscrits || 0} participant(s)`}>
                        <ListChecks size={12} />
                      </button>
                      {f.statut === 'active' && (
                        <button className="header-btn btn-primary btn-sm btn-icon"
                          onClick={() => setModalIns({ formation: f.id })}
                          title="Inscrire un client">
                          <UserPlus size={12} />
                        </button>
                      )}
                      <button className="header-btn btn-secondary btn-sm btn-icon"
                        onClick={() => setModalForm(f)} title="Modifier">
                        <Pencil size={12} />
                      </button>
                      {f.statut === 'active' ? (
                        <button className="header-btn btn-danger btn-sm btn-icon"
                          onClick={() => handleArchiver(f)}
                          disabled={actioning[`arch_${f.id}`]}
                          title="Archiver">
                          <Archive size={12} />
                        </button>
                      ) : (
                        <button className="header-btn btn-secondary btn-sm btn-icon"
                          onClick={() => handleActiver(f)}
                          disabled={actioning[`act_${f.id}`]}
                          title="Réactiver">
                          <RotateCcw size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modals ── */}
      {modalParticipants !== null && (
        <ModalParticipants
          formation={modalParticipants}
          inscriptions={inscriptions}
          onClose={() => setModalParticipants(null)}
          onInscrire={() => setModalIns({ formation: modalParticipants.id })}
          onRefresh={reload}
          dispatch={dispatch}
        />
      )}
      {modalForm !== null && (
        <ModalFormation
          formation={modalForm?.id ? modalForm : null}
          onClose={() => setModalForm(null)}
          onSaved={reload}
        />
      )}
      {modalIns !== null && (
        <ModalInscription
          inscription={modalIns?.id ? modalIns : null}
          preFormation={modalIns?.formation ?? null}
          formations={formations}
          clients={clients || []}
          onClose={() => setModalIns(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}
